import { createFileRoute, Link, useParams, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ArrowLeft, Download, FileText } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  fetchDocumentContent,
  sharePointSourceOf,
  useDocumentDetailQuery,
  type DocumentParty,
  type DocumentSharePointSource,
  type PartyHint,
} from '@/lib/api/documents';
import { useDocumentProcessingQuery } from '@/lib/api/document-processing';
import { DeleteDocumentControl } from '@/components/documents/delete-document-control';
import { AiRunsCard } from '@/components/documents/ai-runs-card';
import { DocumentPartyControl } from '@/components/documents/party-change';
import { SharePointChip } from '@/components/documents/sharepoint-chip';
import { ExcelWorkbook, type ExcelCellSelection } from '@/components/excel/excel-workbook';
import { CellAnnotationModal } from '@/components/excel/cell-annotation-modal';

/**
 * T-0036 / E04-F05 — single-document detail route.
 *
 * Dispatches the render based on the document's mime_type:
 *   - Excel (xlsx / xls): inline Fortune-Sheet viewer with cell-level
 *     annotation (see `apps/web/src/components/excel/`).
 *   - Anything else: plain "Download" link to the content stream.
 *
 * The "Annotate this cell" CTA shows up after the user clicks a single
 * cell; clicking it reveals an inline section (NOT a Radix Dialog per
 * the project's v1 convention) where the user picks a register entry
 * as the source and submits the annotation as an EvidenceLink.
 *
 * T-0211 (E18-F01): a document the connected SharePoint folder took shows
 * the `SharePoint` chip linking to the file. The documents list carries
 * `document_source` for every row; it is warm in the cache on the way
 * rail → grid → document.
 *
 * T-0215 (E19): the party chip — whose document it is — with "Change" for
 * leads, which opens the choice in place under the chips, and the hint when
 * Avero is named on page 1 of a vendor-tagged document.
 *
 * T-0239 (E21-F04): "What the AI did" — every AI worker that looked at the
 * document, from the run ledger, with links into the review queue and Read
 * again for a skipped or failed read.
 */

const EXCEL_MIME_TYPES = new Set<string>([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);

export const Route = createFileRoute('/engagements/$engagementId/documents/$documentId')({
  component: DocumentDetailPage,
});

function DocumentDetailPage() {
  const { engagementId, documentId } = useParams({
    from: '/engagements/$engagementId/documents/$documentId',
  });
  const detail = useDocumentDetailQuery(engagementId, documentId);
  const documents = useDocumentProcessingQuery(engagementId);
  const sharePoint = sharePointSourceOf(documents.data?.documents.find((d) => d.id === documentId));

  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/engagements/$engagementId/documents" params={{ engagementId }}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          <span>Back to documents</span>
        </Link>
      </Button>

      {detail.isLoading ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Spinner /> Loading document…
          </CardContent>
        </Card>
      ) : detail.error ? (
        <ErrorMessage error={detail.error} />
      ) : !detail.data ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Document not found.
          </CardContent>
        </Card>
      ) : (
        <DocumentDetailBody
          engagementId={engagementId}
          documentId={documentId}
          name={detail.data.name}
          mimeType={detail.data.mime_type}
          byteSize={detail.data.byte_size}
          party={detail.data.source_party}
          partyHint={detail.data.party_hint}
          sharePoint={sharePoint}
          uploadStatus={detail.data.upload_status}
        />
      )}
    </div>
  );
}

function DocumentDetailBody({
  engagementId,
  documentId,
  name,
  mimeType,
  byteSize,
  party,
  partyHint,
  sharePoint,
  uploadStatus,
}: {
  engagementId: string;
  documentId: string;
  name: string;
  mimeType: string;
  byteSize: number;
  party: DocumentParty | null;
  partyHint: PartyHint;
  sharePoint: DocumentSharePointSource | null;
  uploadStatus: string;
}) {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);

  // Plain <a href> downloads 401'd — the content route needs the bearer
  // header, which a browser navigation never sends. Fetch with auth and
  // hand the bytes to a synthetic object-URL anchor instead (Tier 3).
  async function handleDownload() {
    setDownloading(true);
    try {
      const buf = await fetchDocumentContent(engagementId, documentId);
      const blob = new Blob([buf], { type: mimeType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  const isExcel = EXCEL_MIME_TYPES.has(mimeType);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle className="truncate" title={name}>
              {name}
            </CardTitle>
            <CardDescription className="font-mono">{documentId.slice(0, 8)}…</CardDescription>
          </div>
          {/* The chips take the full width under the title so the party
              change form and the hint have a whole line each (T-0215). */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Badge variant="outline">{mimeType || 'unknown'}</Badge>
            <SharePointChip source={sharePoint} />
            <Badge variant={uploadStatus === 'stored' ? 'success' : 'secondary'}>
              {uploadStatus}
            </Badge>
            {party ? (
              <DocumentPartyControl
                engagementId={engagementId}
                documentId={documentId}
                party={party}
                partyHint={partyHint}
              />
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span>{formatBytes(byteSize)}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={downloading}
            onClick={() => void handleDownload()}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            <span>{downloading ? 'Downloading…' : 'Download'}</span>
          </Button>
          <DeleteDocumentControl
            engagementId={engagementId}
            documentId={documentId}
            onDeleted={() => {
              void navigate({
                to: '/engagements/$engagementId/documents',
                params: { engagementId },
              });
            }}
            renderRequirementsLink={(label) => (
              <Link
                to="/engagements/$engagementId/selection"
                params={{ engagementId }}
                className="font-medium underline underline-offset-[3px]"
              >
                {label}
              </Link>
            )}
          />
        </CardContent>
      </Card>

      <AiRunsCard
        engagementId={engagementId}
        documentId={documentId}
        renderProposalLink={(proposalId, label, proposal) =>
          proposal.proposal_type === 'discovery_interview' ? (
            // T-0261: a transcript's placement is confirmed on Sessions, not in the review queue.
            <Link
              to="/engagements/$engagementId/discovery/sessions"
              params={{ engagementId }}
              search={{ placement: proposalId }}
              className="text-ink-900 underline underline-offset-[3px]"
              title={label}
            >
              {label}
            </Link>
          ) : proposal.proposal_type.startsWith('discovery_') ? (
            // A Discovery proposal is reviewed on Findings by area, never in
            // the IV&V review queue (ADR-016 §17; T-0257).
            <Link
              to="/engagements/$engagementId/discovery/findings"
              params={{ engagementId }}
              search={{ document: documentId }}
              className="text-ink-900 underline underline-offset-[3px]"
              title={label}
            >
              {label}
            </Link>
          ) : (
            <Link
              to="/engagements/$engagementId/proposals"
              params={{ engagementId }}
              search={{ proposal: proposalId }}
              className="text-ink-900 underline underline-offset-[3px]"
              title={label}
            >
              {label}
            </Link>
          )
        }
      />

      {isExcel ? (
        <ExcelViewerCard engagementId={engagementId} documentId={documentId} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Inline view</CardTitle>
            <CardDescription>
              This file type isn't supported by the inline viewer yet. Use the download link to open
              it locally.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" aria-hidden="true" />
              <span>{mimeType || 'unknown mime type'}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ExcelViewerCard({
  engagementId,
  documentId,
}: {
  engagementId: string;
  documentId: string;
}) {
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [selection, setSelection] = useState<ExcelCellSelection | null>(null);
  const [annotatingSelection, setAnnotatingSelection] = useState<ExcelCellSelection | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBytes(null);
    setLoadError(null);
    fetchDocumentContent(engagementId, documentId)
      .then((buf) => {
        if (!cancelled) setBytes(buf);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [engagementId, documentId]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Workbook</CardTitle>
            <CardDescription>
              Inline render via Fortune-Sheet. Click a cell, then "Annotate this cell" to cite it
              from a register entry as an EvidenceLink.
            </CardDescription>
          </div>
          {selection ? (
            <Button
              onClick={() => setAnnotatingSelection(selection)}
              disabled={annotatingSelection !== null}
            >
              <span>Annotate this cell</span>
            </Button>
          ) : null}
        </div>
        {selection ? (
          <div className="mt-2 text-xs text-muted-foreground">
            Selected: <span className="font-mono">{selection.sheet_name}</span> /{' '}
            <span className="font-mono">{selection.cell_a1}</span>
            {selection.value_snapshot !== null ? (
              <>
                {' '}
                — <span className="font-mono">{selection.value_snapshot}</span>
              </>
            ) : null}
          </div>
        ) : null}
      </CardHeader>
      <CardContent>
        {loadError ? (
          <ErrorMessage error={loadError} />
        ) : !bytes ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner /> Loading workbook…
          </div>
        ) : (
          <ExcelWorkbook bytes={bytes} onCellClick={setSelection} />
        )}
      </CardContent>

      {annotatingSelection ? (
        <CardContent className="border-t pt-4">
          <CellAnnotationModal
            engagementId={engagementId}
            documentId={documentId}
            selection={annotatingSelection}
            onSuccess={() => {
              setAnnotatingSelection(null);
            }}
            onCancel={() => setAnnotatingSelection(null)}
          />
        </CardContent>
      ) : null}
    </Card>
  );
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let idx = 0;
  let val = n;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx += 1;
  }
  return `${val.toFixed(val >= 100 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}
